import moment from 'moment';
import range from 'lodash/range';
import compact from 'lodash/compact';

import {
  CHART_FILTERS_TYPE,
  DATA_SOURCE_TYPE,
  HISTORIC_CALENDAR_TYPE,
  HISTORIC_SHORT_CALENDAR_TYPE,
  HISTORIC_ONE_DAY_CALENDAR_TYPE,
  ORDER_MONTH_IN_YEAR,
  ORDER_TIME_DAY,
  THICKNESS_BY_Y_AXIS,
  VITALS_ACTIVITY_TYPE,
} from './constants';
import { PERIOD } from '../common/Charts/constants';
import {
  DATE_PICKER_DATE_FORMAT,
  DATE_PICKER_DATE_FORMAT_DD_MM_YYYY,
  DATE_PICKER_DATE_FORMAT_YYYY_MM_DD,
} from '../../constants/dates';

const { MONTH, WEEK, QUARTER, DAY, YEAR } = CHART_FILTERS_TYPE;

const CALENDAR_FORMATS = {
  sameDay: 'MMM D',
  nextDay: 'MMM D',
  nextWeek: 'MMM D',
  lastDay: 'MMM D',
  lastWeek: 'MMM D',
  sameElse: 'MMM D',
};

export function makeTickForData(value, type, forYAxis, isWeightInKg) {
  if (value === 0) return '';

  switch (type) {
    case VITALS_ACTIVITY_TYPE.STEPS:
      return (() => {
        if (value === 0) return value;
        if (forYAxis) {
          if (value.toFixed().toString().length < 4) return value.toFixed();
          return `${(value / 1000).toFixed(1)}k`;
        }
        return `${(value / 1000).toFixed()}k`;
      })();
    case VITALS_ACTIVITY_TYPE.DISTANCE:
      return (() => {
        if (value.toFixed().toString().length < 4) {
          return convertValueDistanceToMiles(value, 2);
        }
        return convertValueDistanceToMiles(value);
      })();
    case VITALS_ACTIVITY_TYPE.BMI:
      return (() => {
        return forYAxis ? value.toFixed(1) : value.toFixed(0);
      })();
    case VITALS_ACTIVITY_TYPE.WEIGHT:
      return (() => {
        return formatValueWeightWithMetric(value, isWeightInKg);
      })();
    case VITALS_ACTIVITY_TYPE.HEIGHT:
      return (() => {
        return convertValueToFeetInch(value);
      })();
  }
}

export const convertValueToFeetInch = (value, separate) => {
  if (value === 0) return value;
  const length = (100 * value) / 2.54;
  const feet = Math.floor(length / 12);
  const inch = Math.round(length - 12 * feet);
  return separate ? { feet, inch } : `${feet}'${inch}"`;
};

export const formatValueWeightWithMetric = (value, inKg) => {
  if (inKg) {
    return roundPrecision(value, 1);
  }
  return convertValueWeightToLbs(value);
};

export const formatHeightValueWithMetric = (value, inMeters) => {
  if (inMeters) {
    return roundPrecision(value * 100, 1);
  }
  return convertValueToFeetInch(value);
};

export const formatTemperatureValueWithMetric = (value, inCelsius) => {
  if (inCelsius) {
    return roundPrecision(value, 1, true);
  }
  return roundPrecision(convertCelsiusToFahrenheit(value), 1, true);
};

export const formatGlucoseValueWithMetric = (
  value,
  isMilligramsPerDeciliter,
  forRound,
) => {
  if (isMilligramsPerDeciliter) {
    return roundPrecision(
      forRound ? value : convertMollPreLToMgPerDl(value),
      0,
    );
  }
  return roundPrecision(value, 1, true);
};

export const convertValueToMeters = (feet, inches) => {
  const allFeet = parseFloat(feet) + parseFloat(inches) / 12;
  return allFeet * 0.3048;
};

export const convertValueWeightToLbs = (
  value,
  toFixed = true,
  fractions = 1,
) => {
  return toFixed ? (value / 0.45359237).toFixed(fractions) : value / 0.45359237;
};

export const convertValueWeightToKg = value => {
  return value * 0.45359237;
};

export const convertCelsiusToFahrenheit = value => {
  return value * (9 / 5) + 32;
};

export const convertMollPreLToMgPerDl = value => value * 18;

export function roundPrecision(value, decimals, convertToFloat) {
  if (convertToFloat) {
    return Number(`${Math.round(`${value}e${decimals}`)}e-${decimals}`).toFixed(
      decimals,
    );
  }
  return Number(`${Math.round(`${value}e${decimals}`)}e-${decimals}`);
}

export const convertValueDistanceToMiles = (value, fixedCount) => {
  return (value * 0.000621371192).toFixed(fixedCount || 1);
};

export const getStartDayOfYear = subtractMonthNumber => {
  return moment()
    .subtract(subtractMonthNumber, 'month')
    .format('YYYY-MM-DD');
};

export function numberWithCommas(x) {
  const safeValue = typeof x === 'number' ? x.toString() : x;
  return safeValue ? safeValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '';
}

function getFormatterForDayType(dataType, isHealthRecord = false) {
  if (isHealthRecord) {
    return arrValue => arrValue;
  }
  if (
    [
      VITALS_ACTIVITY_TYPE.BMI,
      VITALS_ACTIVITY_TYPE.WEIGHT,
      VITALS_ACTIVITY_TYPE.HEIGHT,
      VITALS_ACTIVITY_TYPE.SATURATION,
    ].includes(dataType)
  ) {
    return arrValue => {
      const sum = arrValue.reduce((prevValue, nextValue) => {
        return prevValue + nextValue;
      }, 0);
      return arrValue.length > 0 ? sum / arrValue.length : null;
    };
  }
  return arrValue => {
    return arrValue.reduce((prevValue, nextValue) => {
      return prevValue + nextValue;
    }, 0);
  };
}

function formatDataGrafByDay(
  entity,
  type,
  withTime,
  dataType,
  isHealthRecord = false,
) {
  const dayEntityArr = [];
  const dayEntityTimeArr = [];
  if (type === 'value') {
    const formatter = getFormatterForDayType(dataType, isHealthRecord);
    let result = range(24)
      .reduce((acc, _, index) => {
        const baseDate = moment(entity[0].measuredAt);
        const matched = entity
          .filter(({ measuredAt }) => {
            const baseTime = moment(baseDate)
              .startOf('d')
              .add(index, 'h')
              .subtract(1, 'ms');
            const endTime = moment(baseDate)
              .startOf('d')
              .add(index + 1, 'h')
              .subtract(1, 'ms');
            return moment(measuredAt).isBetween(baseTime, endTime);
          })
          .map(item => {
            if (isHealthRecord) {
              return item;
            }
            return item.value;
          });

        return [...acc, matched];
      }, [])
      .map(formatter);

    if (isHealthRecord) {
      const draftRes = [];
      result.forEach((item, index) => {
        if (item.length > 0) {
          item.forEach(data => {
            draftRes.push({
              x: ORDER_TIME_DAY[index],
              y: data.value,
              timeStamp: getMonthLabel(data.measuredAt, 'MMM D, Y, h:mm A'),
            });
          });
        }
      });
      result = draftRes;
    }

    if (withTime) {
      entity.forEach(item => {
        ORDER_TIME_DAY.forEach((time, index) => {
          if (time === getTimeFromDateUTC(item.measuredAt, 'h a', false)) {
            dayEntityTimeArr[index] = getMonthLabel(
              item.measuredAt,
              'MMM D, Y, h:mm A',
            );
          }
        });
      });
    }
    return withTime ? dayEntityTimeArr : result;
  }
  if (type === 'labels') {
    return ORDER_TIME_DAY;
  }
  if (type === 'points') {
    return range(ORDER_TIME_DAY.length).fill(3);
  }
  if (type === 'tooltip') {
    return ORDER_TIME_DAY.map(() => ({ keepTooltipOpen: false }));
  }
  if (type === 'background') {
    ORDER_TIME_DAY.forEach(() => {
      return dayEntityArr.push('rgba(50, 108, 186, 1)');
    });
    return dayEntityArr;
  }
}

function formatDataGrafByWeek(
  entity,
  type,
  withTime,
  dataType,
  isHealthRecord = false,
) {
  const weekEntityArr = [];
  const weekEntityTimeArr = [];

  const baseArr = range(7);

  if (type === 'value') {
    const formatter = getFormatterForDayType(dataType, isHealthRecord);
    const currentDate = moment();
    let result = range(7)
      .reduce((acc, _, index) => {
        const matched = entity
          .filter(({ measuredAt }) => {
            const baseTime = moment(currentDate)
              .subtract(index, 'd')
              .startOf('d')
              .subtract(1, 'ms');
            const endTime = moment(baseTime)
              .add(1, 'd')
              .endOf('d');
            return moment(measuredAt).isBetween(baseTime, endTime);
          })
          .map(item => {
            if (isHealthRecord) {
              return item;
            }
            return item.value;
          });

        return [...acc, matched];
      }, [])
      .map(formatter);

    if (isHealthRecord) {
      const draftRes = [];
      const currentLabelWeek = range(7).map((item, index) => {
        const dateWeek = moment()
          .subtract(index, 'd')
          .format('MMM D, Y');
        return formatDate(dateWeek, 'ddd', true);
      });
      result.forEach((item, index) => {
        if (item.length > 0) {
          item.forEach(data => {
            draftRes.push({
              x: currentLabelWeek[index],
              y: data.value,
              timeStamp: getMonthLabel(data.measuredAt, 'MMM D, Y, h:mm A'),
            });
          });
        }
      });
      result = draftRes;
    }

    if (withTime) {
      const baseTimeStampArr = [];
      range(7).forEach((_, index) => {
        const baseTime = moment.utc(currentDate).subtract(index, 'd');
        const currentLabel = getMonthLabel(baseTime, 'MMM D, Y');
        baseTimeStampArr.push(currentLabel);
      });
      return baseTimeStampArr.reverse();
    }
    return withTime ? weekEntityTimeArr : result.reverse();
  }

  if (type === 'labels') {
    range(7).forEach((item, index) => {
      const currentDate = moment()
        .subtract(index, 'd')
        .format('MMM D, Y');
      return weekEntityArr.push(formatDate(currentDate, 'ddd', true));
    });

    return [...weekEntityArr.reverse()];
  }
  if (type === 'background') {
    range(baseArr.length).forEach(() => {
      return weekEntityArr.push('rgba(50, 108, 186, 1)');
    });
    return weekEntityArr;
  }
  if (type === 'points') {
    return range(baseArr.length).fill(3);
  }
  if (type === 'tooltip') {
    return baseArr.map(() => ({
      keepTooltipOpen: false,
    }));
  }
}

function formatDataGrafByMonth(
  entity,
  type,
  withTime,
  dataType,
  isHealthRecord = false,
) {
  const monthEntityArr = [];
  const monthEntityTimeArr = [];
  const baseArr = range(getLengthMonth());

  if (type === 'value') {
    const formatter = getFormatterForDayType(dataType, isHealthRecord);
    const currentDate = moment();
    let result = baseArr
      .reduce((acc, _, index) => {
        const matched = entity
          .filter(({ measuredAt }) => {
            const baseTime = moment(currentDate)
              .subtract(index, 'd')
              .startOf('d')
              .subtract(1, 'ms');
            const endTime = moment(baseTime)
              .add(1, 'd')
              .endOf('d');
            return moment(measuredAt).isBetween(baseTime, endTime);
          })
          .map(item => {
            if (isHealthRecord) {
              return item;
            }
            return item.value;
          });

        return [...acc, matched];
      }, [])
      .map(formatter);

    if (isHealthRecord) {
      const draftRes = [];
      const currentLabelMonth = [...baseArr].map((item, index) => {
        const currentDateMonth = moment()
          .subtract(index, 'd')
          .format('MMM D, Y');
        return formatDateToTitleMonth(currentDateMonth);
      });
      monthEntityArr.unshift('');
      result.forEach((item, index) => {
        if (item.length > 0) {
          item.forEach(data => {
            draftRes.push({
              x: currentLabelMonth[index],
              y: data.value,
              timeStamp: getMonthLabel(data.measuredAt, 'MMM D, Y, h:mm A'),
            });
          });
        }
      });
      result = draftRes;
    }

    if (withTime) {
      const baseTimeStampArr = [];
      baseArr.forEach((_, index) => {
        const baseTime = moment
          .utc()
          .subtract(index, 'd')
          .format('MMM D, Y');
        baseTimeStampArr.push(baseTime);
      });
      return baseTimeStampArr.reverse();
    }
    return withTime ? monthEntityTimeArr : result.reverse();
  }

  if (type === 'labels') {
    baseArr.forEach((item, index) => {
      const currentDate = moment()
        .subtract(index, 'd')
        .format('MMM D, Y');
      return monthEntityArr.push(formatDateToTitleMonth(currentDate));
    });

    monthEntityArr.unshift('');

    return [...monthEntityArr.reverse()];
  }
  if (type === 'background') {
    baseArr.forEach(() => {
      return monthEntityArr.push('rgba(50, 108, 186, 1)');
    });
    return monthEntityArr;
  }
  if (type === 'points') {
    monthEntityArr.unshift('');
    return range(monthEntityArr.length).fill(3);
  }
  if (type === 'tooltip') {
    return ['', ...baseArr].map(() => ({ keepTooltipOpen: false }));
  }
}

function getPreviousMonth(currentMonthValue, array) {
  const currentIndex = array.indexOf(currentMonthValue);
  if (currentIndex > 0) return array[currentIndex - 1];
  if (currentIndex === 0) return array[array.length - 1];
}

export function getLabelForQuarterAndYear(forQuarter) {
  const baseArr = forQuarter ? range(3) : ORDER_MONTH_IN_YEAR;
  const currentMonthValue = moment();
  const currentMonthLabel = formatDate(currentMonthValue, 'MMM', true);

  const result = [];
  let currentLabel = currentMonthLabel;

  baseArr.forEach(() => {
    result.push(currentLabel);
    currentLabel = getPreviousMonth(currentLabel, ORDER_MONTH_IN_YEAR);
  });

  return { baseArr: result.reverse(), currentDate: currentMonthValue };
}

export function calculateAveragePerMonth(arr) {
  const result = [];
  arr.forEach(item => {
    result.push(item.reduce((a, b) => a + b, 0) / item.length);
  });
  return result;
}

function formatDataGrafByQuarter(entity, type, withTime, dataType) {
  const quarterEntityArr = [];
  const { baseArr, currentDate } = getLabelForQuarterAndYear(true);

  if (type === 'value') {
    const formatter = getFormatterForDayType(dataType);
    return splitEntityByDate(baseArr, entity, currentDate, formatter, withTime);
  }

  if (type === 'labels') {
    return [...baseArr];
  }
  if (type === 'background') {
    range(3).forEach(() => {
      return quarterEntityArr.push('rgba(50, 108, 186, 1)');
    });
    return quarterEntityArr;
  }
  if (type === 'points') {
    return range(quarterEntityArr.length).fill(3);
  }
  if (type === 'tooltip') {
    return range(3).map(() => ({ keepTooltipOpen: false }));
  }
}

function splitEntityByDate(baseArr, entity, currentDate, formatter, withTime) {
  const result = baseArr
    .reduce((acc, _, index) => {
      const matched = entity
        .filter(({ measuredAt }) => {
          const baseTime = moment
            .utc(currentDate)
            .subtract(index, 'month')
            .startOf('month');
          const endTime = moment.utc(baseTime).endOf('month');
          return moment.utc(measuredAt).isBetween(baseTime, endTime);
        })
        .map(({ value }) => value);

      return [...acc, matched];
    }, [])
    .map(formatter);
  if (withTime) {
    const baseTimeStampArr = [];
    baseArr.forEach((_, index) => {
      const baseTime = moment.utc(currentDate).subtract(index, 'month');
      const currentLabel = getMonthLabel(baseTime, 'MMMM, Y');
      baseTimeStampArr.push(currentLabel);
    });
    return baseTimeStampArr.reverse();
  }
  return result.reverse();
}

function formatDataGrafByYear(entity, type, withTime, dataType) {
  const yearEntityArr = [];
  const { baseArr, currentDate } = getLabelForQuarterAndYear(false);
  if (type === 'value') {
    const formatter = getFormatterForDayType(dataType);
    return splitEntityByDate(baseArr, entity, currentDate, formatter, withTime);
  }
  if (type === 'labels') {
    return [...baseArr];
  }
  if (type === 'background') {
    ORDER_MONTH_IN_YEAR.forEach(() => {
      return yearEntityArr.push('rgba(50, 108, 186, 1)');
    });
    return yearEntityArr;
  }
  if (type === 'points') {
    return range(yearEntityArr.length).fill(3);
  }
  if (type === 'tooltip') {
    return [...ORDER_MONTH_IN_YEAR].map(() => ({ keepTooltipOpen: false }));
  }
}

export function adaptInterpolationValue(arr, type, dataType, singleDayMode) {
  if (
    [VITALS_ACTIVITY_TYPE.BMI, VITALS_ACTIVITY_TYPE.WEIGHT].includes(
      dataType,
    ) &&
    arr &&
    ['value', 'points'].includes(type)
  ) {
    if (type === 'value') {
      const tempArr = [...arr];
      const targetLeft = tempArr.find(item => {
        return item > 0;
      });

      arr.push(null);
      arr.unshift(null);

      // eslint-disable-next-line no-param-reassign
      arr[0] = targetLeft;
      if (singleDayMode) {
        let targetRight;
        for (let i = tempArr.length - 1; i >= 0; i--) {
          if (tempArr[i]) {
            targetRight = tempArr[i];
            break;
          }
        }
        // eslint-disable-next-line no-param-reassign
        arr[arr.length - 1] = targetRight;
      }
      return arr;
    }
    // eslint-disable-next-line no-param-reassign
    arr[0] = 0;
    if (singleDayMode) {
      // eslint-disable-next-line no-param-reassign
      arr[arr.length - 1] = 0;
    }
    return arr;
  }
  return arr;
}

function makeAverageValue(rawArr, filterType, dataType) {
  const safeArr = rawArr
    ? rawArr.filter(item => item !== null && item > 0)
    : [];
  return makeAverageOrTotalValue(safeArr, filterType, dataType);
}

export function makeValueForGraf(
  entity,
  type,
  filterType,
  withTime,
  dataType,
  singleDayMode,
  forAverageValue,
  isHealthRecord = false,
) {
  if (
    VITALS_ACTIVITY_TYPE.SATURATION === dataType &&
    type === 'value' &&
    !withTime
  )
    return [];
  switch (filterType) {
    case DAY.type: {
      const rawArr = formatDataGrafByDay(
        entity,
        type,
        withTime,
        dataType,
        isHealthRecord,
      );
      if (forAverageValue) {
        const rowDataForAverageCalc = entity.map(item => item.value);
        return makeAverageValue(rowDataForAverageCalc, filterType, dataType);
      }
      return rawArr || [];
    }
    case WEEK.type: {
      const rawArr = formatDataGrafByWeek(
        entity,
        type,
        withTime,
        dataType,
        isHealthRecord,
      );
      if (forAverageValue) {
        const rowDataForAverageCalc = entity.map(item => item.value);
        return makeAverageValue(rowDataForAverageCalc, filterType, dataType);
      }
      return rawArr || [];
    }
    case MONTH.type: {
      const rawArr = formatDataGrafByMonth(
        entity,
        type,
        withTime,
        dataType,
        isHealthRecord,
      );
      if (forAverageValue) {
        const rowDataForAverageCalc = entity.map(item => item.value);
        return makeAverageValue(rowDataForAverageCalc, filterType, dataType);
      }
      return rawArr || [];
    }

    case QUARTER.type: {
      const rawArr = formatDataGrafByQuarter(entity, type, withTime, dataType);
      if (forAverageValue) {
        if (
          [VITALS_ACTIVITY_TYPE.DISTANCE, VITALS_ACTIVITY_TYPE.STEPS].includes(
            dataType,
          )
        ) {
          return makeAverageValue(rawArr, filterType, dataType);
        }
        const rowDataForAverageCalc = entity.map(item => item.value);
        return makeAverageValue(rowDataForAverageCalc, filterType, dataType);
      }
      return rawArr || [];
    }

    case YEAR.type: {
      const rawArr = formatDataGrafByYear(entity, type, withTime, dataType);
      if (forAverageValue) {
        if (
          [VITALS_ACTIVITY_TYPE.DISTANCE, VITALS_ACTIVITY_TYPE.STEPS].includes(
            dataType,
          )
        ) {
          return makeAverageValue(rawArr, filterType, dataType);
        }
        const rowDataForAverageCalc = entity.map(item => item.value);
        return makeAverageValue(rowDataForAverageCalc, filterType, dataType);
      }
      return rawArr || [];
    }
  }
}

export function formatDate(date, format, inUpperCase) {
  return inUpperCase
    ? moment(date)
        .format(format)
        .toUpperCase()
    : moment(date).format(format);
}

// javascript
export function getTimeFromDateUTC(date, format, useUTC) {
  if (!date) return 'INVALID DATE';

  const inputFormats = [
    DATE_PICKER_DATE_FORMAT,
    DATE_PICKER_DATE_FORMAT_DD_MM_YYYY,
    DATE_PICKER_DATE_FORMAT_YYYY_MM_DD,
    moment.ISO_8601,
  ];
  const parser = useUTC ? moment.utc : moment;

  let m;
  if (moment.isMoment(date)) {
    m = date;
  } else if (date instanceof Date) {
    m = parser(date);
  } else {
    // try strict parsing against known formats (fixes Safari)
    m = parser(date, inputFormats, true);
    if (!m.isValid()) {
      // fallback to permissive parsing (covers other valid inputs)
      m = parser(date);
    }
  }

  return m && m.isValid() ? m.format(format).toUpperCase() : 'INVALID DATE';
}

export const getDateFieldValue = data => {
  if (!data) return undefined;
  const formats = [
    DATE_PICKER_DATE_FORMAT,
    DATE_PICKER_DATE_FORMAT_DD_MM_YYYY,
    DATE_PICKER_DATE_FORMAT_YYYY_MM_DD,
    moment.ISO_8601,
  ];
  let m = moment(data, formats, true); // strict parse with known formats (fixes Safari)
  if (!m.isValid()) m = moment(data); // fallback permissive parse
  return m.isValid() ? m.toDate() : undefined;
};

export function getMonthLabel(date, format) {
  return moment(date).format(format);
}

export function makeDateForAverageOrTotal(data, filterType, singleMode) {
  const format = [YEAR.type, QUARTER.type].includes(filterType)
    ? 'MMM Y'
    : 'MMM D, Y';
  const currentDate = moment().format(format);

  if (singleMode && data) {
    return moment(data[0].measuredAt).format(format);
  }

  if (filterType === DAY.type) {
    return currentDate;
  }

  if (filterType === WEEK.type) {
    const previousDate = moment(currentDate)
      .subtract(1, 'w')
      .add(1, 'd')
      .format(format);
    return `${previousDate} - ${currentDate}`;
  }

  if (filterType === MONTH.type) {
    const previousDate = moment(currentDate)
      .subtract(1, 'month')
      .add(1, 'd')
      .format(format);
    return `${previousDate} - ${currentDate}`;
  }

  if (filterType === QUARTER.type) {
    const previousDate = moment()
      .subtract(2, 'month')
      .format(format);
    return `${previousDate} - ${currentDate}`;
  }

  if (filterType === YEAR.type) {
    const previousDate = moment()
      .subtract(11, 'month')
      .format(format);
    return `${previousDate} - ${currentDate}`;
  }
}

export function formatDateToTitleMonth(date) {
  return moment(date).calendar(null, CALENDAR_FORMATS);
}

export function makeAverageOrTotalValue(data, filterType, dataType) {
  const shouldCalculateAverage = [
    VITALS_ACTIVITY_TYPE.BMI,
    VITALS_ACTIVITY_TYPE.WEIGHT,
    VITALS_ACTIVITY_TYPE.HEIGHT,
  ].includes(dataType);

  if (filterType === DAY.type) {
    if (shouldCalculateAverage) {
      return data.reduce((prev, curr) => prev + curr, 0) / data.length;
    }
    return data.reduce((prev, curr) => prev + curr, 0);
  }
  const resultArr = [];
  data.forEach(item => resultArr.push(item));
  if (filterType === WEEK.type) {
    return resultArr.reduce((a, b) => a + b, 0) / resultArr.length;
  }
  if (filterType === MONTH.type) {
    return resultArr.reduce((a, b) => a + b, 0) / resultArr.length;
  }
  if (filterType === QUARTER.type) {
    return resultArr.reduce((a, b) => a + b, 0) / resultArr.length;
  }
  if (filterType === YEAR.type) {
    return resultArr.reduce((a, b) => a + b, 0) / resultArr.length;
  }
}

export function shouldHideAverageLine(entity, isCandleDataType) {
  if (isCandleDataType) return false;
  const marker = entity.reduce((acc, cur) => {
    if (cur.value) {
      return acc + 1;
    }
    return acc;
  }, 0);
  return marker > 1;
}

export const getDataListByType = ({
  formatMessage,
  type,
  item,
  isWeightInKg,
  isHeightInMeters,
  isMilligramsPerDeciliter,
  allValuesForOneDay,
  safeFormatCalendar,
  isConvertedData,
}) => {
  switch (type) {
    case VITALS_ACTIVITY_TYPE.STEPS:
      return {
        metric: formatMessage({
          id: 'patient.healthboard.steps.metric',
        }),
        measuredAt: formatToCalendarTime(
          item.measuredAt,
          false,
          HISTORIC_SHORT_CALENDAR_TYPE,
        ),
        value: numberWithCommas(item.value),
        rowDate: item.measuredAt,
        dataSource: getOriginalProviderCode(item.originalProviderCodes),
      };
    case VITALS_ACTIVITY_TYPE.BMI:
      return {
        metric: '',
        measuredAt: formatToCalendarTime(
          item.measuredAt,
          false,
          allValuesForOneDay
            ? safeFormatCalendar
            : HISTORIC_SHORT_CALENDAR_TYPE,
        ),
        value:
          typeof item.value === 'string'
            ? parseFloat(item.value).toFixed(1)
            : item.value.toFixed(1),
        rowDate: item.measuredAt,
        dataSource: getOriginalProviderCode(item.originalProviderCodes),
      };
    case VITALS_ACTIVITY_TYPE.DISTANCE:
      return {
        metric: formatMessage({
          id: 'patient.healthboard.distance.metric',
        }),
        measuredAt: formatToCalendarTime(
          item.measuredAt,
          false,
          HISTORIC_SHORT_CALENDAR_TYPE,
        ),
        value: convertValueDistanceToMiles(item.value),
        rowDate: item.measuredAt,
        dataSource: getOriginalProviderCode(item.originalProviderCodes),
      };
    case VITALS_ACTIVITY_TYPE.WEIGHT:
      return {
        metric: isWeightInKg
          ? formatMessage({
              id: 'patient.healthboard.weight.metric.kg',
            })
          : formatMessage({
              id: 'patient.healthboard.weight.metric',
            }),
        measuredAt: formatToCalendarTime(
          item.measuredAt,
          false,
          allValuesForOneDay
            ? safeFormatCalendar
            : HISTORIC_SHORT_CALENDAR_TYPE,
        ),
        value: formatValueWeightWithMetric(item.value, isWeightInKg),
        rowDate: item.measuredAt,
        dataSource: getOriginalProviderCode(item.originalProviderCodes),
      };
    case VITALS_ACTIVITY_TYPE.HEIGHT:
      return {
        metric: isHeightInMeters
          ? formatMessage({
              id: 'patient.healthboard.height.metric.cm',
            })
          : '',
        measuredAt: formatToCalendarTime(
          item.measuredAt,
          false,
          HISTORIC_CALENDAR_TYPE,
        ),
        value: formatHeightValueWithMetric(item.value, isHeightInMeters),
        rowDate: item.measuredAt,
        dataSource: getOriginalProviderCode(item.originalProviderCodes),
      };
    case VITALS_ACTIVITY_TYPE.SATURATION:
      return {
        metric: formatMessage({
          id: 'patient.healthboard.saturation.metric',
        }),
        measuredAt: formatToCalendarTime(
          item.measuredAt,
          false,
          allValuesForOneDay
            ? HISTORIC_ONE_DAY_CALENDAR_TYPE
            : HISTORIC_SHORT_CALENDAR_TYPE,
        ),
        value: getSafeSaturationHistoricValue(item, allValuesForOneDay),
        rowDate: item.measuredAt,
        dataSource: getOriginalProviderCode(item.originalProviderCodes),
      };
    case VITALS_ACTIVITY_TYPE.HEART_RATE:
      return {
        metric: formatMessage({
          id: 'patient.healthboard.heart_rate.metric',
        }),
        measuredAt: formatToCalendarTime(
          item.measuredAt,
          false,
          allValuesForOneDay
            ? HISTORIC_ONE_DAY_CALENDAR_TYPE
            : HISTORIC_SHORT_CALENDAR_TYPE,
        ),
        value: getSafeSaturationHistoricValue(item, allValuesForOneDay),
        rowDate: item.measuredAt,
        dataSource: getOriginalProviderCode(item.originalProviderCodes),
      };
    case VITALS_ACTIVITY_TYPE.BLOOD_GLUCOSE:
      return {
        metric: isMilligramsPerDeciliter
          ? formatMessage({
              id:
                'patient.healthboard.blood_glucose.metric.milligrams_per_deciliter',
            })
          : formatMessage({
              id:
                'patient.healthboard.blood_glucose.metric.millimoles_per_liter',
            }),
        measuredAt: formatToCalendarTime(
          item.measuredAt,
          false,
          allValuesForOneDay
            ? HISTORIC_ONE_DAY_CALENDAR_TYPE
            : HISTORIC_SHORT_CALENDAR_TYPE,
        ),
        value: getSafeBloodGlucoseHistoricValue(
          item,
          allValuesForOneDay,
          isMilligramsPerDeciliter,
          isConvertedData,
        ),
        rowDate: item.measuredAt,
        dataSource: getOriginalProviderCode(item.originalProviderCodes),
      };
    default:
      return {};
  }
};

export const chooseProviderData = entity => {
  return entity.find(({ isPriority }) => !!isPriority);
};

export const chooseAvailableSourceData = entity => {
  return entity && entity.length > 0
    ? entity.find(({ measurements }) => measurements.length > 0)
    : [];
};

export function concatDataHistoricValue(data, dataType) {
  const isCandleType = [
    VITALS_ACTIVITY_TYPE.SATURATION,
    VITALS_ACTIVITY_TYPE.HEART_RATE,
    VITALS_ACTIVITY_TYPE.BLOOD_GLUCOSE,
  ].includes(dataType);
  const isAccumulateDataType = [
    VITALS_ACTIVITY_TYPE.DISTANCE,
    VITALS_ACTIVITY_TYPE.WEIGHT,
    VITALS_ACTIVITY_TYPE.STEPS,
    VITALS_ACTIVITY_TYPE.HEIGHT,
    VITALS_ACTIVITY_TYPE.BMI,
  ].includes(dataType);

  if (data.length > 0) {
    const safeDate = [...data];
    const baseArrDate = [];
    const resultArr = [];
    const concatValueArr = [];
    const draftResultForSaturation = [];

    safeDate.forEach(item => {
      baseArrDate.push(
        getTimeFromDateUTC(item.measuredAt, 'YYYY-MM-DD', false),
      );
    });
    const safeArrDate = [...new Set(baseArrDate)];

    safeArrDate.forEach((item, index) => {
      if (isCandleType) {
        safeDate.forEach(entity => {
          if (moment(item, 'YYYY-MM-DD').isSame(entity.measuredAt, 'day')) {
            if (!draftResultForSaturation[index]) {
              draftResultForSaturation.push({
                originalProviderCodes: entity.originalProviderCodes[0],
                measuredAt: entity.measuredAt,
                value: [entity.value],
              });
            } else {
              const previousProviderCode =
                draftResultForSaturation[index].originalProviderCodes;
              const isSameProviderCode =
                previousProviderCode === entity.originalProviderCodes[0];
              draftResultForSaturation[index].value.push(entity.value);
              if (!isSameProviderCode) {
                draftResultForSaturation[index].originalProviderCodes =
                  DATA_SOURCE_TYPE.ALL_SOURCES;
              }
            }
          }
        });
      }

      if (isAccumulateDataType) {
        safeDate.forEach(entity => {
          if (moment(item, 'YYYY-MM-DD').isSame(entity.measuredAt, 'day')) {
            if (!resultArr[index]) {
              resultArr.push({ ...entity, counter: 1 });
            } else {
              const previousProviderCode =
                resultArr[index].originalProviderCodes[0];
              const isSameProviderCode =
                previousProviderCode === entity.originalProviderCodes[0];
              const currentValue = resultArr[index].value + entity.value;
              resultArr[index].value = currentValue;
              const previousCounter = concatValueArr[index]
                ? concatValueArr[index].counter
                : 1;
              concatValueArr[index] = {
                measuredAt: entity.measuredAt,
                value: currentValue,
                counter: previousCounter + 1,
                providerCode: isSameProviderCode
                  ? resultArr[index].providerCode
                  : DATA_SOURCE_TYPE.ALL_SOURCES,
                originalProviderCodes: isSameProviderCode
                  ? previousProviderCode
                  : [DATA_SOURCE_TYPE.ALL_SOURCES],
              };
            }
          }
        });
      }
    });

    if (isCandleType) {
      draftResultForSaturation.forEach(
        ({ measuredAt, value, originalProviderCodes }) => {
          resultArr.push({
            measuredAt,
            originalProviderCodes: [originalProviderCodes],
            value: {
              min: Math.min(...value),
              max: Math.max(...value),
            },
          });
        },
      );
    }

    if (isAccumulateDataType) {
      concatValueArr.forEach((item, index) => {
        if (item) {
          if (item.counter > 1) {
            resultArr[index] = { ...item, value: item.value / item.counter };
          } else {
            resultArr[index] = item;
          }
        }
      });
    }

    return [...resultArr];
  }

  return [];
}

export function formatToCalendarTime(timestamp, useUTC, calendarFormat) {
  return useUTC
    ? moment.utc(timestamp).calendar(null, calendarFormat || CALENDAR_FORMATS)
    : moment(timestamp).calendar(null, calendarFormat || CALENDAR_FORMATS);
}

export function getTitleAndDescriptionEmptyState({
  formatMessage,
  type,
  isProvider,
}) {
  if (
    [
      VITALS_ACTIVITY_TYPE.DISTANCE,
      VITALS_ACTIVITY_TYPE.STEPS,
      VITALS_ACTIVITY_TYPE.SATURATION,
      VITALS_ACTIVITY_TYPE.BLOOD_GLUCOSE,
    ].includes(type)
  ) {
    return {
      titleButton: formatMessage({
        id: 'patient.portal.emptyState.goToSettings',
      }),
      description: isProvider
        ? formatMessage({
            id: 'patient.healthboard.graf.emptyState',
          })
        : formatMessage({
            id: 'patient.portal.emptyState.stepsOrDistance.noData',
          }),
      descriptionForAccount: formatMessage({
        id: 'patient.portal.emptyState.stepsOrDistance.addAccount',
      }),
      withOutCapitalize: true,
    };
  }
  if (VITALS_ACTIVITY_TYPE.BMI === type) {
    return {
      titleButton: formatMessage(
        { id: 'patient.portal.emptyState.addData' },
        {
          type: formatMessage({
            id: 'patient.portal.emptyState.bodyInfo',
          }),
        },
      ),
      description: formatMessage({ id: 'patient.healthboard.graf.emptyState' }),
    };
  }
  if (
    [
      VITALS_ACTIVITY_TYPE.HEART_RATE,
      VITALS_ACTIVITY_TYPE.BODY_SURFACE_AREA,
      VITALS_ACTIVITY_TYPE.BLOOD_PRESSURE,
      VITALS_ACTIVITY_TYPE.RESPIRATORY_RATE,
      VITALS_ACTIVITY_TYPE.BODY_TEMPERATURE,
    ].includes(type)
  ) {
    return {
      description: formatMessage({ id: 'patient.healthboard.graf.emptyState' }),
    };
  }

  return {
    titleButton: formatMessage(
      { id: 'patient.portal.emptyState.addData' },
      {
        type,
      },
    ),
    description: formatMessage({ id: 'patient.healthboard.graf.emptyState' }),
  };
}

export function validateFutureTime(formatMessage, time, pickDate) {
  if (
    moment(pickDate, 'YYYY-MM-DD')
      .add(1, 'm')
      .isBefore(moment(new Date(), 'YYYY-MM-DD'))
  ) {
    return null;
  }
  const parseTime = moment(time, 'HH:mm a');
  const isFutureTime = parseTime.isAfter(moment(new Date(), 'HH:mm a'));

  return isFutureTime
    ? formatMessage({ id: 'patient.portal.errorTime' })
    : null;
}

export function getCurrentTime() {
  return moment().format('hh:mm a');
}

export const getSafeSaturationHistoricValue = (item, allValuesForOneDay) => {
  const dataMin = allValuesForOneDay ? item.value : item.value.min;
  const dataMax = allValuesForOneDay ? item.value : item.value.max;

  if (!dataMin && !dataMax) return '-';
  const safeMin = Math.round(dataMin);
  const safeMax = Math.round(dataMax);
  return safeMin === safeMax ? safeMin : `${safeMin} - ${safeMax}`;
};

const getSafeBloodGlucoseHistoricValue = (
  item,
  allValuesForOneDay,
  isMilligramsPerDeciliter,
  isConvertedData,
) => {
  if (allValuesForOneDay) {
    if (!item.value) return '-';

    return isConvertedData
      ? item.value
      : formatGlucoseValueWithMetric(item.value, isMilligramsPerDeciliter);
  }

  const { min, max } = { ...item.value };

  if (!min && !max) return '-';

  if (min === max)
    return isConvertedData
      ? min
      : formatGlucoseValueWithMetric(min, isMilligramsPerDeciliter);

  const dataMin = isConvertedData
    ? min
    : formatGlucoseValueWithMetric(min, isMilligramsPerDeciliter);
  const dataMax = isConvertedData
    ? max
    : formatGlucoseValueWithMetric(max, isMilligramsPerDeciliter);

  return `${dataMin} - ${dataMax}`;
};

export const makeSplitDataForSaturation = (
  data,
  filter,
  isCandleType,
  isWithoutLimit,
) => {
  const transparentDataEntity = [];
  const valueDataEntity = [];
  if (!isCandleType) return { transparentDataEntity, valueDataEntity };
  if (data && data.measurements && data.measurements.length > 0) {
    const isQuarterFilter = filter === QUARTER.type;
    const isDayFilter = filter === DAY.type;
    const safeDate = [...data.measurements];
    const baseArrDate = [];
    const resultArr = [];
    const draftResultForSaturation = [];
    safeDate.forEach(item => {
      baseArrDate.push(
        getTimeFromDateUTC(item.measuredAt, 'YYYY-MM-DD', false),
      );
    });
    const safeArrDate = [...new Set(baseArrDate)];

    if (!isDayFilter) {
      safeArrDate.forEach((item, index) => {
        const startTime = moment(item)
          .startOf('d')
          .subtract(1, 'm');
        const endTime = moment(startTime)
          .add(1, 'd')
          .add(1, 'm');

        safeDate.forEach(entity => {
          if (moment(entity.measuredAt).isBetween(startTime, endTime)) {
            if (!draftResultForSaturation[index]) {
              draftResultForSaturation.push({
                measuredAt: getTimeFromDateUTC(
                  entity.measuredAt,
                  'YYYY-MM-DD',
                  false,
                ),
                value: [entity.value],
              });
            } else {
              draftResultForSaturation[index].value.push(entity.value);
            }
          }
        });
      });
      draftResultForSaturation.forEach(({ measuredAt, value }) => {
        const baseTime = moment(measuredAt);
        const currentLabel = getMonthLabel(baseTime, 'MMM D, Y');
        resultArr.push({
          currentLabel,
          value: {
            min: Math.min(...value),
            max: Math.max(...value),
          },
        });
      });
    }

    if ([WEEK.type, MONTH.type].includes(filter)) {
      const safeDeltaScale = calculateDeltaScale(resultArr, true);

      resultArr.forEach(item => {
        const safeMaxValue =
          item && item.value.max > 100 && !isWithoutLimit
            ? 100
            : item.value.max;
        const safeMinValue =
          item && item.value.min > 100 && !isWithoutLimit
            ? 100
            : item.value.min;
        transparentDataEntity.push({
          date: item.currentLabel,
          value: safeMinValue,
          originMax: item.value.max,
          originMin: item.value.min,
        });
        const safeDeltaValue =
          safeMaxValue - safeMinValue < 1
            ? safeMaxValue - safeMinValue + safeDeltaScale
            : safeMaxValue - safeMinValue;
        valueDataEntity.push({
          date: item.currentLabel,
          value: safeDeltaValue,
          originMax: item.value.max,
          originMin: item.value.min,
        });
      });
    }

    if ([QUARTER.type, YEAR.type].includes(filter)) {
      const { baseArr, currentDate } = getLabelForQuarterAndYear(
        isQuarterFilter,
      );
      const draftResult = baseArr
        .reduce((acc, _, index) => {
          const matched = resultArr
            .filter(({ currentLabel }) => {
              const baseTime = moment(currentDate)
                .subtract(index, 'month')
                .startOf('month');
              const endTime = moment(baseTime).endOf('month');
              return moment(currentLabel).isBetween(
                baseTime.subtract(1, 'm'),
                endTime,
              );
            })
            .map(({ value }) => value);
          return [...acc, matched];
        }, [])
        // eslint-disable-next-line array-callback-return
        .map(item => {
          if (item.length !== 0) {
            const minArr = [];
            const maxArr = [];
            item.forEach(({ min, max }) => {
              minArr.push(min);
              maxArr.push(max);
            });
            return { min: Math.min(...minArr), max: Math.max(...maxArr) };
          }
        });

      const safeDeltaScale = calculateDeltaScale(draftResult);

      draftResult.reverse().forEach(item => {
        const safeMaxValue =
          item && item.max > 100 && !isWithoutLimit
            ? { ...item, max: 100 }
            : item;
        transparentDataEntity.push(
          item
            ? {
                value: item.min,
                originMin: item.min,
                originMax: item.max,
              }
            : null,
        );
        valueDataEntity.push(
          item
            ? {
                value:
                  safeMaxValue.max - item.min < 1
                    ? safeMaxValue.max - item.min + safeDeltaScale
                    : safeMaxValue.max - item.min,
                originMin: item.min,
                originMax: item.max,
              }
            : null,
        );
      });
    }

    if (isDayFilter) {
      const draftDayResult = range(24)
        .reduce((acc, _, index) => {
          const baseDate = moment(data.measurements[0].measuredAt);
          const matched = data.measurements
            .filter(({ measuredAt }) => {
              const baseTime = moment(baseDate)
                .startOf('d')
                .add(index, 'h')
                .subtract(1, 'ms');
              const endTime = moment(baseDate)
                .startOf('d')
                .add(index + 1, 'h')
                .subtract(1, 'ms');
              return moment(measuredAt).isBetween(baseTime, endTime);
            })
            .map(({ value }) => value);
          return [...acc, matched];
        }, [])
        .map(item => {
          if (item.length > 0) {
            const rawMax = Math.max(...item);
            const safeMax = rawMax > 100 && !isWithoutLimit ? 100 : rawMax;
            const rawMin = Math.min(...item);
            const safeMin = rawMin > 100 && !isWithoutLimit ? 100 : rawMin;
            const isSinglePoint = rawMax === rawMin || safeMax - rawMin <= 1;

            return isSinglePoint
              ? {
                  min: safeMin,
                  max: safeMax,
                  originMin: rawMin,
                  originMax: rawMax,
                  singlePoint: true,
                }
              : {
                  min: safeMin,
                  max: safeMax,
                  originMin: rawMin,
                  originMax: rawMax,
                };
          }
          return null;
        });

      const safeDeltaScale = calculateDeltaScale(draftDayResult);

      draftDayResult.forEach(item => {
        transparentDataEntity.push(
          item
            ? {
                value: item.singlePoint ? item.min - safeDeltaScale : item.min,
                originMax: item.originMax,
                originMin: item.originMin,
              }
            : null,
        );
        valueDataEntity.push(
          item
            ? {
                value: item.singlePoint
                  ? item.max - item.min + safeDeltaScale
                  : item.max - item.min,
                originMax: item.originMax,
                originMin: item.originMin,
              }
            : null,
        );
      });
    }
  }

  return { transparentDataEntity, valueDataEntity };
};

export const makeValueForBothDataset = ({
  baseTimeArr,
  transparentEntity,
  valueEntity,
  activeFilter,
  dataType,
  isWithoutLimit,
  isMilligramsPerDeciliter,
}) => {
  let valueData = [];
  let transparentData = [];
  const ranges = [];

  const withoutRoundForGlucose =
    dataType === VITALS_ACTIVITY_TYPE.BLOOD_GLUCOSE &&
    !isMilligramsPerDeciliter;

  if ([QUARTER.type, YEAR.type, DAY.type].includes(activeFilter)) {
    valueData = [
      ...valueEntity.map(item => {
        return item ? item.value : null;
      }),
    ];
    transparentData = [
      ...transparentEntity.map(item => {
        if (!item) return null;
        return withoutRoundForGlucose ? item.value : Math.round(item.value);
      }),
    ];
    valueEntity.forEach((item, index) => {
      if (item) {
        ranges[index] = {
          min: withoutRoundForGlucose
            ? transparentEntity[index].originMin
            : Math.round(transparentEntity[index].originMin),
          max: withoutRoundForGlucose
            ? transparentEntity[index].originMax
            : Math.round(transparentEntity[index].originMax),
        };
      }
    });
  } else {
    baseTimeArr.forEach((baseDate, index) => {
      valueEntity.forEach((item, i) => {
        if (baseDate === item.date) {
          valueData[index] = item.value;
          ranges[index] = {
            min: withoutRoundForGlucose
              ? transparentEntity[i].originMin
              : Math.round(transparentEntity[i].originMin),
            max: withoutRoundForGlucose
              ? transparentEntity[i].originMax
              : Math.round(transparentEntity[i].originMax),
          };
        }
      });
      transparentEntity.forEach(item => {
        if (baseDate === item.date) {
          transparentData[index] = withoutRoundForGlucose
            ? item.value
            : Math.round(item.value);
        }
      });
    });
  }

  const maxValueForRange = ranges.reduce(
    (prev, current) => (prev.max > current.max ? prev : current),
    {},
  ).max;

  const minValueForRange = ranges.reduce(
    (prev, current) => (prev.min < current.min ? prev : current),
    {},
  ).min;

  const safeMaxValueForRange = normalizeMinMaxValue(
    maxValueForRange,
    isWithoutLimit,
  );
  const safeMinValueForRange = normalizeMinMaxValue(
    minValueForRange,
    isWithoutLimit,
  );

  const minLimitForYAxis = withoutRoundForGlucose
    ? safeMinValueForRange - (safeMaxValueForRange - safeMinValueForRange) * 0.2
    : Math.floor(
        safeMinValueForRange -
          (safeMaxValueForRange - safeMinValueForRange) * 0.2,
      );

  const maxLimitForYAxis = withoutRoundForGlucose
    ? safeMaxValueForRange + (safeMaxValueForRange - safeMinValueForRange) * 0.2
    : Math.ceil(
        safeMaxValueForRange +
          (safeMaxValueForRange - safeMinValueForRange) * 0.2,
      );

  return {
    valueData,
    transparentData,
    ranges,
    maxValueForRange,
    minValueForRange,
    minLimitForYAxis,
    maxLimitForYAxis,
  };
};

export const getCategoryPercentageWithFilter = (
  filter,
  singleDayMode,
  isSinglePointAtFirstPosition,
  forOneSource,
) => {
  switch (filter) {
    case DAY.type:
      return singleDayMode
        ? getCategoryPercentageWithOneSource(0.7, forOneSource)
        : getCategoryPercentageWithOneSource(0.8, forOneSource);
    case WEEK.type:
      return isSinglePointAtFirstPosition
        ? getCategoryPercentageWithOneSource(0.03, forOneSource)
        : getCategoryPercentageWithOneSource(0.3, forOneSource);
    case QUARTER.type:
      return getCategoryPercentageWithOneSource(0.13, forOneSource);
    case MONTH.type:
      return isSinglePointAtFirstPosition
        ? getCategoryPercentageWithOneSource(0.03, forOneSource)
        : getCategoryPercentageWithOneSource(0.6, forOneSource);
    case YEAR.type:
      return getCategoryPercentageWithOneSource(0.45, forOneSource);
  }
};

export const normalizeMinMaxValue = (value, isWithoutLimit) => {
  if (isWithoutLimit) return value;
  if (value > 100) {
    return 100;
  }
  if (value < 0) {
    return 0;
  }
  return value;
};

export const getSafeDescriptionDataHistoricList = (
  type,
  formatMessage,
  short = false,
) => {
  switch (type) {
    case VITALS_ACTIVITY_TYPE.DISTANCE:
    case VITALS_ACTIVITY_TYPE.STEPS: {
      return formatMessage({
        id: `patient.healthboard.total${!short ? 'Data' : ''}`,
      });
    }
    case VITALS_ACTIVITY_TYPE.BMI:
    case VITALS_ACTIVITY_TYPE.WEIGHT: {
      return formatMessage({
        id: `patient.healthboard.average${!short ? 'Data' : ''}`,
      });
    }
    case VITALS_ACTIVITY_TYPE.HEART_RATE:
    case VITALS_ACTIVITY_TYPE.SATURATION:
    case VITALS_ACTIVITY_TYPE.BLOOD_GLUCOSE: {
      return formatMessage({
        id: `patient.healthboard.minMax${!short ? 'Data' : ''}`,
      });
    }
    default:
      return '';
  }
};

export const checkForAvailableSourceData = rowData => {
  return rowData && rowData.measurements && rowData.measurements.length !== 0;
};

export const getSourceDataWithType = (data, type) => {
  return data.find(({ provider }) => {
    return provider.code === type;
  });
};

export const createTooltips = data => {
  if (data && data.length > 0) {
    const tooltipsArr = range(data.length).map(() => []);
    data.forEach((dataset, index) => {
      tooltipsArr[index].push(
        dataset.data.map(() => ({ keepTooltipOpen: false })),
      );
    });
    return tooltipsArr;
  }
  return [];
};

export const adaptHistoricResponse = rowData => {
  return rowData && rowData.length > 0
    ? rowData.map(item => {
        return {
          ...item,
          value: item.value.baseValue,
        };
      })
    : [];
};

export const adaptResponse = (rowData, convertingOptionData) => {
  if (rowData && rowData.length > 0) {
    return rowData.map(item => {
      const isAvailableMeasurements =
        item.measurements && item.measurements.length > 0;
      return {
        ...item,
        measurements: isAvailableMeasurements
          ? item.measurements.map(entity => {
              return {
                ...entity,
                value: convertingOptionData
                  ? getConvertedSafeData(
                      entity.value.baseValue,
                      convertingOptionData,
                    )
                  : entity.value.baseValue,
              };
            })
          : [],
      };
    });
  }
  return [];
};

export const getLengthMonth = () => {
  const now = moment();
  const end = moment().subtract(1, 'month');
  const duration = moment.duration(now.diff(end));
  return duration.asDays();
};

export const calculateAverageValue = data => {
  const safeArr = compact(data);
  return (
    safeArr.reduce(
      (accumulator, currentEntity) => accumulator + currentEntity,
      0,
    ) / safeArr.length
  );
};

export const calculateDeltaScale = (dataArr, weekOrMonth) => {
  const safeArr = weekOrMonth
    ? dataArr.map(item => ({
        ...item,
        min: item.value.min,
        max: item.value.max,
      }))
    : dataArr;
  const maxValueForRange = compact([...safeArr]).reduce(
    (prev, current) => (prev.max > current.max ? prev : current),
    {},
  ).max;

  const minValueForRange = compact([...safeArr]).reduce(
    (prev, current) => (prev.min < current.min ? prev : current),
    {},
  ).min;

  const deltaScale =
    ((maxValueForRange - minValueForRange) * THICKNESS_BY_Y_AXIS) / 15;
  return deltaScale < 0.1 ? 0.1 : deltaScale;
};

export const calculateMinMaxRangeForRawValue = rawData => {
  if (rawData && rawData.length > 0) {
    const allValues = [];
    rawData.forEach(({ measurements }) => {
      if (measurements && measurements.length > 0) {
        measurements.forEach(({ value }) => {
          if (value && value.baseValue) allValues.push(value.baseValue);
          if (value && value.bpDiastolic) {
            allValues.push(value.bpDiastolic);
            allValues.push(value.bpSystolic);
          }
        });
      }
    });

    const rawMin = Math.min(...allValues);
    const rawMax = Math.max(...allValues);

    const calculatedRawMin =
      rawMin - (rawMax - rawMin === 0 ? rawMax : rawMax - rawMin) * 0.2;
    const calculatedRawMax =
      rawMax + (rawMax - rawMin === 0 ? rawMax : rawMax - rawMin) * 0.2;

    return {
      min: calculatedRawMin && calculatedRawMin > 0 ? calculatedRawMin : 0,
      max: calculatedRawMax && calculatedRawMax > 0 ? calculatedRawMax : 0,
    };
  }

  return { min: 0, max: 0 };
};

export const getCategoryPercentageWithOneSource = (value, forOneSource) =>
  forOneSource ? value / 2 : value;

export const getConvertedSafeData = (value, optionConverting) => {
  const { type, isMilligramsPerDeciliter, forRound } = optionConverting;
  switch (type) {
    case VITALS_ACTIVITY_TYPE.BLOOD_GLUCOSE:
      return formatGlucoseValueWithMetric(
        value,
        isMilligramsPerDeciliter,
        forRound,
      );
    default:
      return value;
  }
};

export const getOriginalProviderCode = originalProviderCodes => {
  if (!originalProviderCodes) {
    return '';
  }
  if (typeof originalProviderCodes === 'string') {
    return originalProviderCodes;
  }

  return originalProviderCodes.length === 1
    ? originalProviderCodes[0]
    : DATA_SOURCE_TYPE.ALL_SOURCES;
};

const getSourceCodeByPeriod = (
  sourceDataCodes,
  tooltipDate,
  comparePeriod,
  getSafeDataSourceTitle,
  formatMessage,
) => {
  const filteredDataCodes = sourceDataCodes.filter(el => {
    return moment(new Date(tooltipDate)).isSame(
      new Date(el.measuredAt),
      comparePeriod,
    );
  });
  const providerCodes = filteredDataCodes.map(el => el.providerCode);
  const uniqProviderCodes = [...new Set(providerCodes.flat(1))];
  return getSafeDataSourceTitle(
    getOriginalProviderCode(uniqProviderCodes),
    formatMessage,
  );
};

export const sourceDataCodeFilteredByDate = (
  sourceDataCodes,
  period,
  tooltipDate,
  formatMessage,
  getSafeDataSourceTitle,
) => {
  switch (period) {
    case DAY.type: {
      const filteredDataCodes = sourceDataCodes.filter(el => {
        const roundDown = moment(new Date(el.measuredAt))
          .startOf('minute')
          .toString();
        return moment(new Date(tooltipDate)).isSame(new Date(roundDown));
      });
      const providerCodes = filteredDataCodes.map(el => el.providerCode);
      const uniqProviderCodes = [...new Set(providerCodes.flat(1))];
      return getSafeDataSourceTitle(
        getOriginalProviderCode(uniqProviderCodes),
        formatMessage,
      );
    }
    case WEEK.type:
    case MONTH.type: {
      return getSourceCodeByPeriod(
        sourceDataCodes,
        tooltipDate,
        PERIOD.DAY,
        getSafeDataSourceTitle,
        formatMessage,
      );
    }

    case QUARTER.type:
    case YEAR.type: {
      return getSourceCodeByPeriod(
        sourceDataCodes,
        tooltipDate,
        PERIOD.MONTH,
        getSafeDataSourceTitle,
        formatMessage,
      );
    }
  }
};

export const getTooltipDate = (
  sourceDataCode,
  data,
  tooltip,
  isCandleDataType,
  isGrafTypeLine,
  filterType,
) => {
  if (isCandleDataType) {
    return sourceDataCode === DATA_SOURCE_TYPE.CCD
      ? data.timeSets.ccd[tooltip.index]
      : data.timeSets.devices[tooltip.index];
  }
  if (isGrafTypeLine) {
    const forQuarterYear = [QUARTER.type, YEAR.type].includes(filterType);

    if (sourceDataCode === DATA_SOURCE_TYPE.CCD) {
      return forQuarterYear
        ? data.timeSets[tooltip.index]
        : data.datasets[tooltip.datasetIndex].data[tooltip.index].timeStamp;
    }
    return data.timeSets[tooltip.index] || '';
  }
  return data.timeSets[tooltip.index] || '';
};



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/utils.js